import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { ChevronDown, PictureInPicture2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SummarySection } from '@/components/desktop-summary/summary-primitives';
import {
  hideAllPip,
  interactWithPipItem,
  setPipConversationVisible,
  setPipPlacement,
  usePipTaskState,
} from '@/features/picture-in-picture/picture-in-picture-host';
import {
  PIP_CASCADE,
  PipCompleted,
  PipMedia,
  pipItemBox,
  pipItemSize,
  useRenderedPipItems,
} from '@/features/picture-in-picture/pip-media';
import { isElectronDesktop } from '@/lib/electron-bridge';

/** Items in the panel are at most this large (Codex's 400). */
const HOME_MAX_SIZE = 400;

/** The width an element takes, followed as it changes (a callback ref: it may mount later). */
function useWidth() {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (element === null) return;
    setWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, width] as const;
}

/**
 * What agents drive for the conversation, as Codex's "Computer Use" section of its summary: the
 * Picture in Picture row (hide it for this chat or all active ones, move it to the floating window
 * and back) and, at home here, the stack of the Mac window and tabs they use, lead at the top
 * right and the others cascading behind it. A click on the lead brings what it shows to the
 * front, on another one makes it lead. Shown while an agent drives something for the
 * conversation, unless the window shows its browser already.
 */
export function SummaryPictureInPictureSection({ conversation }: { conversation: string }) {
  const state = usePipTaskState();
  const current = state.conversations[conversation];
  const hidden = state.hiddenConversationIds.includes(conversation);
  const rendered = useRenderedPipItems(
    current !== undefined && current.placement === 'home' ? current.items : [],
  );
  const [surface, width] = useWidth();
  // The window shows the conversation's browser already: nothing here, as in Codex.
  if (!isElectronDesktop() || current === undefined || current.presented) return null;
  const items = rendered.filter((item) => !item.exiting);
  const leadId = items[0]?.id;
  const displaySize = Math.min(
    HOME_MAX_SIZE,
    Math.max(1, width - Math.max(0, items.length - 1) * PIP_CASCADE.x),
  );
  // The lead's top right corner is the surface's; the others cascade left and down.
  const boxes = rendered.map((item) =>
    pipItemBox(
      { x: width, y: 0 },
      'top-right',
      item.exiting ? 0 : items.indexOf(item),
      pipItemSize(displaySize, item.aspect),
    ),
  );
  const height = Math.max(0, ...boxes.map((box) => box.top + box.height));
  return (
    <div>
      <SummarySection title="Computer Use">
        <div className="flex min-h-8 items-center gap-2 text-sm">
          <PictureInPicture2 className="size-4 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">Picture in Picture</span>
          {hidden ? (
            <Button
              onClick={() => setPipConversationVisible(conversation, true)}
              size="xs"
              variant="ghost"
            >
              Show
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button aria-label="Hide Picture in Picture" size="xs" variant="ghost">
                  Hide
                  <ChevronDown className="size-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setPipConversationVisible(conversation, false)}>
                  Hide for this chat
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => hideAllPip()}>
                  Hide for all active chats
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {current.placement === 'home' ? (
                  <DropdownMenuItem onSelect={() => setPipPlacement(conversation, 'pinned')}>
                    Move to floating window
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => setPipPlacement(conversation, 'home')}>
                    Move to sidebar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div ref={surface} className="relative mx-2" style={{ height }}>
          {rendered.map((item, position) => {
            const index = item.exiting ? 0 : items.indexOf(item);
            return (
              <button
                key={item.id}
                aria-label={item.title}
                className={`absolute cursor-pointer overflow-hidden rounded-[8px] bg-neutral-900 shadow-[0_6px_20px_rgb(0_0_0/0.16)] transition-[left,top,width,height,filter] duration-300 hover:brightness-95 ${
                  item.exiting ? 'pip-exit' : 'pip-enter'
                }`}
                onClick={() => interactWithPipItem(item.id, item.id === leadId)}
                style={{ ...boxes[position], zIndex: 100 - index }}
                title={item.title}
                type="button"
              >
                <PipMedia item={item} />
                {item.state === 'completed' && <PipCompleted icon={item.icon} />}
              </button>
            );
          })}
        </div>
      </SummarySection>
    </div>
  );
}
